import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { coursesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  GraduationCap, Plus, Search, BookOpen, Video, FileQuestion,
  Tag, Layers
} from 'lucide-react';
import { toast } from 'sonner';

const DRAGON_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/fyvt2ajz_Transparent%20BG%20Dragon.png';

const categoryColors = {
  general: 'bg-blue-100 text-blue-700',
  compliance: 'bg-purple-100 text-purple-700',
  onboarding: 'bg-green-100 text-green-700',
  certification: 'bg-orange-100 text-orange-700',
};

const Courses = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '', description: '', category: 'general', tags: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const params = {};
        if (categoryFilter !== 'all') params.category = categoryFilter;
        const res = await coursesApi.getAll(params);
        setCourses(res.data);
      } catch { toast.error('Failed to load courses'); }
      finally { setLoading(false); }
    };
    fetchCourses();
  }, [categoryFilter]);

  const filteredCourses = courses.filter(c =>
    c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCourse.title.trim()) return;
    setCreating(true);
    try {
      const data = {
        title: newCourse.title,
        description: newCourse.description,
        category: newCourse.category,
        tags: newCourse.tags ? newCourse.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        lessons: [],
        quiz: []
      };
      const res = await coursesApi.create(data);
      setCourses([res.data, ...courses]);
      setCreateDialogOpen(false);
      setNewCourse({ title: '', description: '', category: 'general', tags: '' });
      toast.success('Course created!');
      navigate(`/courses/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create course');
    } finally { setCreating(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="courses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={DRAGON_MASCOT} alt="Courses" className="w-12 h-12" />
          <div>
            <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Courses</h1>
            <p className="text-navy/60 mt-0.5">Internal training and compliance education</p>
          </div>
        </div>
        {user?.role === 'admin' && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="create-course-btn">
                <Plus className="w-4 h-4 mr-2" /> New Course
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-['Amaranth']">Create Course</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Course Title *</Label>
                  <Input value={newCourse.title} onChange={e => setNewCourse({ ...newCourse, title: e.target.value })} placeholder="e.g., BSA/AML Fundamentals" required data-testid="course-title-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={newCourse.description} onChange={e => setNewCourse({ ...newCourse, description: e.target.value })} placeholder="What this course covers..." rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={newCourse.category} onValueChange={v => setNewCourse({ ...newCourse, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                      <SelectItem value="onboarding">Onboarding</SelectItem>
                      <SelectItem value="certification">Certification</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input value={newCourse.tags} onChange={e => setNewCourse({ ...newCourse, tags: e.target.value })} placeholder="BSA, AML, training" />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating} className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="submit-course-btn">
                    {creating ? 'Creating...' : 'Create Course'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search courses..."
            className="pl-9"
            data-testid="courses-search"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]" data-testid="courses-category-filter">
            <Layers className="w-4 h-4 mr-2 text-navy/40" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="compliance">Compliance</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="certification">Certification</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Course grid */}
      {filteredCourses.length === 0 ? (
        <Card className="border-navy/10 border-dashed">
          <CardContent className="py-16 text-center">
            <GraduationCap className="w-12 h-12 mx-auto text-navy/20 mb-3" />
            <p className="text-navy/50 font-medium">No courses yet</p>
            <p className="text-sm text-navy/40 mt-1">{user?.role === 'admin' ? 'Create your first course to get started' : 'Check back soon for new courses'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCourses.map(course => (
            <Card
              key={course.id}
              className="border-navy/10 hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate(`/courses/${course.id}`)}
              data-testid={`course-card-${course.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <Badge className={categoryColors[course.category] || categoryColors.general}>
                    {course.category}
                  </Badge>
                  <div className="flex items-center gap-2 text-xs text-navy/40">
                    {course.lessons?.length > 0 && (
                      <span className="flex items-center gap-0.5"><BookOpen className="w-3 h-3" />{course.lessons.length}</span>
                    )}
                    {course.quiz?.length > 0 && (
                      <span className="flex items-center gap-0.5"><FileQuestion className="w-3 h-3" />{course.quiz.length}Q</span>
                    )}
                  </div>
                </div>
                <h3 className="font-bold text-[#1A2B5E] text-lg group-hover:text-[#106EFD] transition-colors line-clamp-2">{course.title}</h3>
                {course.description && (
                  <p className="text-sm text-navy/60 mt-2 line-clamp-3">{course.description}</p>
                )}
                {course.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {course.tags.slice(0, 3).map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-[10px]"><Tag className="w-2.5 h-2.5 mr-0.5" />{tag}</Badge>
                    ))}
                    {course.tags.length > 3 && <span className="text-[10px] text-navy/40">+{course.tags.length - 3}</span>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Courses;
